import { Moment } from 'moment'
import {
  Action,
  AsyncState,
  loadingAsyncState,
  none,
  Option,
  some,
  unloadedAsyncState
} from 'widgets-for-react'
import {
  getBooking,
  getRefundingSpecification,
  postRefund,
  PostRefundResponse,
  RefundingSpecification,
  removeEscrow,
  RemoveEscrowResponse,
  Transaction
} from './bookingDetailsApi'

export type Props = {
  booking_identifier: string
  loading_gif: string
}

export type ArrivalDateMutation = {
  from?: Moment
  to: Moment
  updated_at: Moment
}

export type AppPayment = {
  id: number
  due_date: Moment
  i18n_key: string
  is_paid: boolean
  merchant_reference: string
  amount: number
  opp_transaction_uid: string
}

export type FeeSpecification = {
  rental_amount: number
  cleaning_fee: number
  linnen_fee: number
  towel_fee: number
  booking_fee: number
  tourist_tax_amount: number
  additional_fees: Array<{
    amount: number
    description: string
  }>
  total: number
  commission_percentage: number
  commission_discount_percentage: number
  commissionable_amount: number
  commission_amount_ex_vat: number
  commission_vat_amount: number
  vat_percentage: number
}

export type Booking = {
  tenant: {
    id: number
    full_name: string
  }
  owner: {
    id: number
    full_name: string
    opp_merchant_uid: string
  }
  payment_provider: 'adyen' | 'opp'
  identifier: string
  arrival_date: Moment
  departure_date: Moment
  advert: {
    id: number
    name: string
  }
  humanized_state_for_superadmin: string
  is_booking_completed: boolean
  amount_to_disburse?: number
  is_disbursed: boolean
  is_request_extendable: boolean
  replacement_booking: Option<string>
  arrival_date_history: ArrivalDateMutation[]
  payments: AppPayment[]
  fee_specification: FeeSpecification
}

export type State =
  | {
      kind: 'loading'
      booking: AsyncState<Booking>
    }
  | {
      kind: 'loaded'
      booking: Booking
      detailedRentalAmount: boolean
      refunding: Option<{
        specification: AsyncState<RefundingSpecification>
        amount: number
        refunding: AsyncState<PostRefundResponse>
        removeEscrow: AsyncState<RemoveEscrowResponse>
      }>
    }

export type LayoutProps = {
  state: State
  toggleDetailedRentalAmount: () => void
  loading_gif: string
  onRefundAmountChange: (_: number) => void
  onRefundSubmit: () => void
  onRemoveEscrow: (_: Transaction) => void
}

export const initialState = (props: Props): State => ({
  kind: 'loading',
  booking: loadingAsyncState(_ => getBooking(props.booking_identifier))
})

export const getRefundingErrors = (
  refundingSpecification: RefundingSpecification,
  amount: number
): {
  amount: string[]
} => ({
  amount: [
    amount <= 0 ? I18n.t('errors.messages.greater_than', { count: 0 }) : null,
    amount > refundingSpecification.refundable_amount
      ? I18n.t('errors.messages.less_than_or_equal_to', {
          count: refundingSpecification.refundable_amount
        })
      : null,
    !amount && amount !== 0 ? I18n.t('errors.messages.blank') : null
  ].filter(x => !!x)
})

const initialBookingLoadedState = (
  booking: Booking,
  refundingState: AsyncState<PostRefundResponse>,
  removeEscrowState: AsyncState<RemoveEscrowResponse>
): State => ({
  kind: 'loaded',
  booking,
  detailedRentalAmount: false,
  refunding:
    booking.payment_provider === 'opp' && !!booking.owner.opp_merchant_uid
      ? some({
          specification: loadingAsyncState(() =>
            getRefundingSpecification(booking.identifier)
          ),
          amount: 0,
          message: '',
          removeEscrow: removeEscrowState,
          refunding: refundingState
        })
      : none()
})

export const bookingDetailsUpdaters = {
  onBookingUpdate: (
    updater: Action<AsyncState<Booking>>
  ): Action<State> => s => {
    if (s.kind !== 'loading') {
      return s
    }
    const newBooking = updater(s.booking)

    return newBooking.kind === 'loaded'
      ? initialBookingLoadedState(
          newBooking.value,
          unloadedAsyncState(),
          unloadedAsyncState()
        )
      : {
          ...s,
          booking: newBooking
        }
  },

  onToggleDetailedRentalAmount: (): Action<State> => s =>
    s.kind !== 'loaded'
      ? s
      : {
          ...s,
          detailedRentalAmount: !s.detailedRentalAmount
        },
  onRefundAmountChange: (amount: number): Action<State> => s =>
    s.kind === 'loaded' && s.refunding.v.kind === 'r'
      ? {
          ...s,
          refunding: {
            ...s.refunding,
            v: {
              ...s.refunding.v,
              v: {
                ...s.refunding.v.v,
                amount
              }
            }
          }
        }
      : s,
  onRefundSubmit: (): Action<State> => s => {
    if (
      s.kind !== 'loaded' ||
      s.refunding.v.kind === 'l' ||
      s.refunding.v.v.specification.kind !== 'loaded'
    ) {
      return s
    }
    const amount = s.refunding.v.v.amount

    const errors = getRefundingErrors(
      s.refunding.v.v.specification.value,
      amount
    )
    if (errors.amount.length > 0) {
      return s
    }
    return {
      ...s,
      refunding: {
        ...s.refunding,
        v: {
          ...s.refunding.v,
          v: {
            ...s.refunding.v.v,
            refunding: loadingAsyncState(() =>
              postRefund(s.booking.identifier, amount)
            )
          }
        }
      }
    }
  },
  onRefundingSpecificationUpdate: (
    updater: Action<AsyncState<RefundingSpecification>>
  ): Action<State> => s =>
    s.kind === 'loaded' && s.refunding.v.kind === 'r'
      ? {
          ...s,
          refunding: {
            ...s.refunding,
            v: {
              ...s.refunding.v,
              v: {
                ...s.refunding.v.v,
                specification: updater(s.refunding.v.v.specification)
              }
            }
          }
        }
      : s,

  onPostRefundingUpdate: (
    updater: Action<AsyncState<PostRefundResponse>>
  ): Action<State> => s => {
    if (s.kind !== 'loaded' || s.refunding.v.kind === 'l') {
      return s
    }

    const update = updater(s.refunding.v.v.refunding)
    return s.refunding.v.v.refunding.kind === 'loading' &&
      update.kind !== 'loading'
      ? initialBookingLoadedState(s.booking, update, unloadedAsyncState())
      : {
          ...s,
          refunding: {
            ...s.refunding,
            v: {
              ...s.refunding.v,
              v: {
                ...s.refunding.v.v,
                refunding: update
              }
            }
          }
        }
  },
  onRemoveEscrow: (t: Transaction): Action<State> => s =>
    s.kind !== 'loaded' || s.refunding.v.kind === 'l'
      ? s
      : {
          ...s,
          refunding: {
            ...s.refunding,
            v: {
              ...s.refunding.v,
              v: {
                ...s.refunding.v.v,
                removeEscrow: loadingAsyncState(() => removeEscrow(t))
              }
            }
          }
        },
  onRemoveEscrowUpdate: (
    updater: Action<AsyncState<RemoveEscrowResponse>>
  ): Action<State> => s => {
    if (s.kind !== 'loaded' || s.refunding.v.kind === 'l') {
      return s
    }

    const update = updater(s.refunding.v.v.removeEscrow)
    if (
      s.refunding.v.v.removeEscrow.kind === 'loading' &&
      update.kind !== 'loading'
    ) {
      return initialBookingLoadedState(s.booking, unloadedAsyncState(), update)
    }

    return {
      ...s,
      refunding: {
        ...s.refunding,
        v: {
          ...s.refunding.v,
          v: {
            ...s.refunding.v.v,
            removeEscrow: update
          }
        }
      }
    }
  }
}
